import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import { toastWarn } from '../Toast/ToastComponent';

// ─── helpers ────────────────────────────────────────────────────────────────

const applyHeaderStyle = (row) => {
  row.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFC000' }, // orange – matches existing sheets
    };
    cell.font = { bold: true, size: 12, name: 'Times New Roman' };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin', color: { argb: '000000' } },
      left: { style: 'thin', color: { argb: '000000' } },
      bottom: { style: 'thin', color: { argb: '000000' } },
      right: { style: 'thin', color: { argb: '000000' } },
    };
  });
};

const applyDataStyle = (row) => {
  row.eachCell((cell) => {
    cell.font = { size: 11, name: 'Times New Roman' };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: { style: 'thin', color: { argb: '000000' } },
      left: { style: 'thin', color: { argb: '000000' } },
      bottom: { style: 'thin', color: { argb: '000000' } },
      right: { style: 'thin', color: { argb: '000000' } },
    };
  });
};

const getCellText = (value) => {
  if (value === null || value === undefined) return '--';
  return String(value);
};

const autoSizeColumns = (worksheet, minWidth = 12, maxWidth = 50) => {
  worksheet.columns.forEach((column) => {
    const headerText = getCellText(column.header);
    // Start from header length with extra padding to ensure headers are never cut off
    let longest = headerText.length + 4;

    column.eachCell({ includeEmpty: true }, (cell) => {
      const cellLen = getCellText(cell.value).length;
      longest = Math.max(longest, cellLen);
    });

    column.width = Math.min(Math.max(longest + 4, minWidth), maxWidth);
  });
};

// ─── Survey Report List (main table) ────────────────────────────────────────

/**
 * Downloads the survey report list (SurveyReportComp) as an Excel file.
 * @param {Array}  rows      – processed row objects
 * @param {string} fileName  – e.g. 'Survey_Report.xlsx'
 */
export const downloadSurveyReportExcel = async (rows = [], fileName = 'Survey_Report.xlsx') => {
  if (!Array.isArray(rows) || rows.length === 0) {
    toastWarn('No data available to download');
    return false;
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Survey Report');

  worksheet.columns = [
    { header: 'S.NO',               key: 'serialNo',         width: 8  },
    { header: 'SURVEY NAME',        key: 'survey_name',      width: 30 },
    { header: 'NO OF QUESTIONS',    key: 'no_of_questions',  width: 18 },
    { header: 'PERIOD',             key: 'period',           width: 18 },
    { header: 'START DATE',         key: 'start_date',       width: 16 },
    { header: 'END DATE',           key: 'end_date',         width: 16 },
    { header: 'TOTAL ASSIGNED',     key: 'totalAssigned',    width: 16 },
    { header: 'TOTAL ATTENDED',     key: 'totalAttended',    width: 16 },
    { header: 'TOTAL NOT ATTENDED', key: 'totalNotAttended', width: 20 },
  ];

  // Style header row
  applyHeaderStyle(worksheet.getRow(1));
  worksheet.getRow(1).height = 28;

  rows.forEach((row) => {
    const addedRow = worksheet.addRow({
      serialNo:         row.serialNo         ?? '--',
      survey_name:      row.survey_name      ?? '--',
      no_of_questions:  row.no_of_questions  ?? '--',
      period:           row.period           ?? '--',
      start_date:       row.start_date       ?? '--',
      end_date:         row.end_date         ?? '--',
      totalAssigned:    row.totalAssigned    ?? '--',
      totalAttended:    row.totalAttended    ?? '--',
      totalNotAttended: row.totalNotAttended ?? '--',
    });
    applyDataStyle(addedRow);
  });

  autoSizeColumns(worksheet, 10, 32);

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), fileName);
  return true;
};

// ─── Survey Users List (SurveyBasedUsersComp) ───────────────────────────────

/**
 * Downloads the per-survey user list as an Excel file.
 * @param {Array}  rows      – user row objects
 * @param {string} fileName  – e.g. 'Survey_Users.xlsx'
 */
export const downloadSurveyUsersExcel = async (rows = [], dynamicCols = [], fileName = 'Survey_Users.xlsx') => {
  if (!Array.isArray(rows) || rows.length === 0) {
    toastWarn('No data available to download');
    return false;
  }

  const workbook = new ExcelJS.Workbook();
  
  const defaultCols = [
    { header: 'S.NO', key: 'serialNo', width: 8 },
    { header: 'EMPLOYEE CODE', key: 'employeeCode', width: 20 },
    { header: 'USER NAME', key: 'userName', width: 25 },
  ];

  const excelCols = dynamicCols
    .filter((col) => col.field !== 'serialNo' && col.field !== 'userName' && col.field !== 'action' && col.field !== 'employeeCode')
    .map((col) => {
      const headerLen = (col.headerName || '').length;
      // Give question columns a width proportional to their text length
      const computedWidth = Math.min(Math.max(headerLen + 4, 20), 80);
      return {
        header: col.headerName,
        key: col.field,
        width: computedWidth,
      };
    });

  const allCols = [...defaultCols, ...excelCols];

  // Group rows by prefix
  const groups = {};

  rows.forEach((row) => {
    const empCode = String(row.employeeCode || '').trim();
    const uName = String(row.userName || '').trim();

    // "if employee code uisernmae not exit don't show in sheet"
    if (!empCode || empCode === '--' || empCode.toLowerCase() === 'undefined' || 
        !uName || uName === '--' || uName.toLowerCase() === 'undefined') {
      return;
    }

    const rowData = { ...row };
    excelCols.forEach(col => {
       if (!rowData[col.key]) {
         rowData[col.key] = '--';
       }
    });

    // Extract prefix dynamically: letters before any numbers, fallback to 'OTHER'
    const match = empCode.match(/^[a-zA-Z]+/);
    const prefix = match ? match[0].toUpperCase() : 'OTHER';

    if (!groups[prefix]) {
      groups[prefix] = [];
    }
    groups[prefix].push(rowData);
  });

  if (Object.keys(groups).length === 0) {
    toastWarn('No downloadable user records with valid names and codes.');
    return false;
  }

  // Sort worksheets: OWS -> QCI -> others alphabetically -> 'OTHER'
  const prefixOrder = ['OWS', 'QCI'];
  const sortedPrefixes = Object.keys(groups).sort((a, b) => {
    const idxA = prefixOrder.indexOf(a);
    const idxB = prefixOrder.indexOf(b);
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    if (a === 'OTHER') return 1;
    if (b === 'OTHER') return -1;
    return a.localeCompare(b);
  });

  sortedPrefixes.forEach(prefix => {
    const sheetName = prefix.length <= 25 ? `${prefix} Users` : prefix.substring(0, 25);
    const sheet = workbook.addWorksheet(sheetName);
    sheet.columns = allCols;

    applyHeaderStyle(sheet.getRow(1));
    sheet.getRow(1).height = 28;

    let sno = 1;
    groups[prefix].forEach(rowData => {
      rowData.serialNo = sno++;
      const addedRow = sheet.addRow(rowData);
      applyDataStyle(addedRow);
    });

    autoSizeColumns(sheet, 10, 80);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), fileName);
  return true;
};
